import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, claimGroup } from '../lib/api';
import api from '../lib/api';
import { ArrowLeft, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { teeTimeForGroup, formatPlayingHandicap, getEffectivePH, getAllowedPH } from '../golf';
import { groupDisplayName, sortedGroupIndexes } from '../lib/groupOrder';
import { effectiveCourseForPlayer } from '../lib/effectiveCourse';
import { getGroupClaimData, setGroupClaimData } from '../lib/ownerAuth';
import { getTripAccess } from '../lib/tripAccess';
import { roundHasScores } from '../lib/formatHelpers';
import { resumeHole } from '../lib/scoringResume';
import { toast } from 'sonner';

export default function TeamsTeeTimes() {
  const { code, roundId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null); // group idx being claimed
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getTrip(code);
      setTrip(res.data);
      const r = res.data.rounds?.find(r => r.id === roundId);
      if (r) setRound(r);
    } catch {} finally { setLoading(false); }
  }, [code, roundId]);

  useEffect(() => { load(); }, [load]);

  const players = trip?.players || [];
  const teams = round?.teams || [];
  const sorted = round ? sortedGroupIndexes(round) : teams.map((_, i) => i);
  const access = getTripAccess(code);
  const isOrganiser = access?.role === 'organiser' || !!access?.legacy;
  const drawLocked = round?.teams_locked === true;
  const hasScores = round ? roundHasScores(round) : false;
  const showResetBtn = isOrganiser && drawLocked;

  const handleResetDraw = async () => {
    setResetting(true);
    try {
      await api.post(`/trips/${code}/rounds/${roundId}/reset-draw`);
      setResetModalOpen(false);
      toast.success('Draw reset. You can now redo the draw.');
      load();
    } catch (e) {
      setResetModalOpen(false);
      if (e.response?.status === 409) {
        toast.error("Draw can't be reset once scoring has started");
        load();
      } else {
        toast.error('Failed to reset draw. Please try again.');
      }
    } finally { setResetting(false); }
  };

  const handleStartScoring = async (idx) => {
    setClaiming(idx);
    try {
      const res = await claimGroup(code, roundId, { group_idx: idx, claimant_name: 'Scorer' });
      setGroupClaimData(code, roundId, { token: res.data.token, group_idx: idx, expires_at: res.data.expires_at });
      // Store group in localStorage for the scoring screen to pick up
      localStorage.setItem(`foretour:groupPick:${code}:${roundId}`, String(idx));
      navigate(`/trip/${code}/round/${roundId}/scoring`);
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(`Group already claimed by ${e.response?.data?.claimed_by || 'someone'}.`);
        load(); // refresh to show updated claim state
      } else {
        toast.error('Unable to claim group.');
      }
    } finally { setClaiming(null); }
  };

  const handleContinueScoring = (idx) => {
    localStorage.setItem(`foretour:groupPick:${code}:${roundId}`, String(idx));
    navigate(`/trip/${code}/round/${roundId}/scoring`);
  };

  if (loading) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="teams-tee-times-screen">
      {/* Header */}
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(`/trip/${code}/round/${roundId}/setup`)} className="text-white p-1" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" weight="bold" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl text-white font-bold">Teams & Tee Times</h1>
          <p className="text-xs text-[#B8C7C0]">{round?.label} · {round?.course?.name}</p>
        </div>
      </div>

      <div className="px-4 pt-4 pb-8 max-w-[900px] mx-auto space-y-3">
        {teams.length === 0 ? (
          <div className="rounded-xl border border-[#E1E6E4] bg-white p-12 text-center" data-testid="empty-state">
            <p className="text-[#455561] text-sm">Teams not drawn yet</p>
          </div>
        ) : (
          sorted.map(idx => {
            const teamPids = teams[idx] || [];
            const teeTime = round ? teeTimeForGroup(round, idx) : null;
            const groupName = round ? groupDisplayName(round, idx) : `Group ${idx + 1}`;

            // Claim state — same logic as Scoring.jsx group picker
            const claims = round?.group_claims || [];
            const claim = claims.find(c => c.group_idx === idx);
            const claimActive = claim && new Date(claim.expires_at) > new Date();
            const myClaimData = getGroupClaimData(code, roundId);
            const isMyGroup = myClaimData?.group_idx === idx && myClaimData?.token;
            const claimedByOther = claimActive && !isMyGroup;
            const isFinished = Boolean(round?.group_finished?.[idx]);

            // Button state
            let btnLabel, btnDisabled, btnVariant, btnHandler, btnHelper;
            if (isFinished) {
              if (isOrganiser) {
                btnLabel = 'Review scores'; btnDisabled = false; btnVariant = 'default';
                btnHandler = () => handleContinueScoring(idx);
                btnHelper = 'Round finished';
              } else if (isMyGroup) {
                btnLabel = 'View scores'; btnDisabled = false; btnVariant = 'default';
                btnHandler = () => navigate(`/trip/${code}/round/${roundId}/scorecard`);
                btnHelper = 'Round finished';
              } else {
                btnLabel = 'Finished'; btnDisabled = true; btnVariant = 'ghost';
                btnHelper = 'Round finished';
              }
            } else if (claimedByOther) {
              btnLabel = 'In play'; btnDisabled = true; btnVariant = 'ghost';
            } else if (isMyGroup) {
              btnLabel = 'Continue scoring'; btnDisabled = false; btnVariant = 'default';
              btnHandler = () => handleContinueScoring(idx);
            } else {
              btnLabel = 'Start scoring'; btnDisabled = false; btnVariant = 'default';
              btnHandler = () => handleStartScoring(idx);
            }

            return (
              <div key={idx}
                className="rounded-xl border border-[#E1E6E4] overflow-hidden"
                data-testid={`tee-card-${idx}`}>
                {/* Card header */}
                <div className="bg-[#1B4332] px-3 py-2 flex items-center justify-between">
                  <span className="text-sm font-bold text-white">{groupName}</span>
                  {teeTime ? (
                    <span className="text-sm font-bold text-[#C9A24A] tabular-nums font-mono" data-testid={`tee-time-${idx}`}>{teeTime}</span>
                  ) : (
                    <span className="text-xs text-white/50">No tee time</span>
                  )}
                </div>
                {/* Player rows */}
                <div className="divide-y divide-[#F4F6F5]">
                  {teamPids.map(pid => {
                    const p = players.find(pl => pl.id === pid);
                    if (!p) return null;
                    const course = round ? effectiveCourseForPlayer(round, pid) : null;
                    const ph = getAllowedPH(p, course, round);
                    return (
                      <div key={pid} className="px-3 py-2 bg-white flex items-center justify-between gap-2" data-testid={`player-row-${pid}`}>
                        <span className="text-sm text-[#1D242B] break-words">{p.name}</span>
                        <span className="text-xs text-[#8A9BA3] flex-shrink-0 tabular-nums">PH {formatPlayingHandicap(ph)}</span>
                      </div>
                    );
                  })}
                </div>
                {/* Start/Continue scoring button */}
                <div className="px-3 py-2 bg-white border-t border-[#F4F6F5]">
                  <Button
                    onClick={btnHandler}
                    disabled={btnDisabled || claiming === idx}
                    className={`w-full h-9 rounded-lg text-sm font-semibold ${
                      btnDisabled
                        ? 'bg-[#F4F6F5] text-[#B8C7C0] cursor-not-allowed'
                        : isMyGroup
                          ? 'bg-[#1B4332] text-white hover:bg-[#2D5A47]'
                          : 'bg-[#1B4332] text-white hover:bg-[#2D5A47]'
                    }`}
                    data-testid={`scoring-btn-${idx}`}>
                    {claiming === idx ? 'Claiming...' : btnLabel}
                  </Button>
                  {btnHelper && (
                    <p className="text-[10px] text-[#8A9BA3] text-center mt-1" data-testid={`group-helper-${idx}`}>{btnHelper}</p>
                  )}
                </div>
              </div>
            );
          })
        )}

        {/* Reset Draw — organiser only, draw locked */}
        {showResetBtn && (
          <div className="pt-2 space-y-1.5" data-testid="reset-draw-section">
            <Button
              onClick={() => setResetModalOpen(true)}
              disabled={hasScores}
              className={`w-full h-10 rounded-xl text-sm font-semibold gap-2 ${
                hasScores
                  ? 'bg-[#F4F6F5] text-[#B8C7C0] cursor-not-allowed border border-[#E1E6E4]'
                  : 'bg-white text-[#D32F2F] border border-[#E1E6E4] hover:bg-red-50'
              }`}
              variant="outline"
              data-testid="reset-draw-btn">
              <ArrowCounterClockwise className="w-4 h-4" weight="bold" />
              Reset draw
            </Button>
            {hasScores && (
              <p className="text-xs text-[#D32F2F] text-center" data-testid="reset-draw-disabled-msg">
                Draw can't be reset once scoring has started
              </p>
            )}
          </div>
        )}

        {/* Reset Draw Confirmation Modal */}
        {resetModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6" data-testid="reset-draw-modal">
            <div className="bg-white rounded-2xl p-5 max-w-sm w-full space-y-4 shadow-xl">
              <h3 className="text-base font-bold text-[#1D242B]">Reset the draw for {round?.label || 'this round'}?</h3>
              <p className="text-sm text-[#455561]">
                Groups, teams and tee times for this round will be cleared so you can redo the draw. Other rounds are not affected.
              </p>
              <div className="flex gap-2 pt-1">
                <Button
                  onClick={() => setResetModalOpen(false)}
                  disabled={resetting}
                  className="flex-1 h-10 rounded-xl text-sm font-semibold bg-[#F4F6F5] text-[#455561] hover:bg-[#E1E6E4]"
                  variant="ghost"
                  data-testid="reset-draw-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={handleResetDraw}
                  disabled={resetting}
                  className="flex-1 h-10 rounded-xl text-sm font-semibold bg-[#D32F2F] text-white hover:bg-[#B71C1C]"
                  data-testid="reset-draw-confirm">
                  {resetting ? 'Resetting...' : 'Reset draw'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
